import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  CreateSubscriptionDto,
  scoreToReportType,
  SubscriptionPayload,
} from './reports-service';

export interface ReportFormData {
  id?: string;
  reportType?: 'entity' | 'portfolio' | string;
  active: boolean;
  reportObject?: string;
  portfolioName?: string;
  entityName?: string;
  schedule?: string;
  score: string;
  recipients: string[];
  isNew: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ReportsSetupFormService {
  private defaultReportForm: ReportFormData = {
    id: undefined,
    reportType: 'entity',
    active: true,
    reportObject: undefined,
    schedule: undefined,
    score: '',
    recipients: [],
    isNew: true,
  };

  private currentReportSubject = new BehaviorSubject<ReportFormData>(
    this.defaultReportForm,
  );
  public currentReport$ = this.currentReportSubject.asObservable();

  constructor() {}

  getCurrentReport(): ReportFormData {
    return this.currentReportSubject.getValue();
  }

  setCurrentReport(report: ReportFormData): void {
    this.currentReportSubject.next(report);
  }

  createNewReport(): void {
    this.currentReportSubject.next({ ...this.defaultReportForm });
  }

  resetForm(): void {
    this.currentReportSubject.next({ ...this.defaultReportForm });
  }

  setupReportFromSubscription(subscription: ReportFormData): void {
    const reportForm: ReportFormData = {
      id: subscription.id,
      reportType: subscription.reportType || 'entity',
      active: subscription.active,
      entityName: subscription.entityName,
      portfolioName: subscription.portfolioName,
      reportObject: subscription.reportObject,
      schedule: subscription.schedule,
      score: subscription.score,
      recipients: subscription.recipients,
      isNew: false,
    };

    this.setCurrentReport(reportForm);
  }

  /**
   * Convert form data into the wire payload for create/update.
   *
   * For portfolios, `portfolioName` and `companyIds` must be resolved by the
   * caller (look the portfolio up before calling) — the backend's
   * `PortfolioPayload` requires both.
   */
  convertFormToSubscription(
    formData: ReportFormData,
    portfolioName?: string,
    companyIds?: string[],
  ): CreateSubscriptionDto & { id?: string } {
    const dto: CreateSubscriptionDto & { id?: string } = {
      schedule: formData.schedule || '',
      reportType: scoreToReportType(formData.score),
      reportFormat: 'pdf',
      recipients: formData.recipients,
      payload: this.buildPayload(formData, portfolioName, companyIds),
    };

    if (formData.id) {
      dto.id = formData.id;
    }

    return dto;
  }

  private buildPayload(
    formData: ReportFormData,
    portfolioName?: string,
    companyIds?: string[],
  ): SubscriptionPayload {
    if (formData.reportType === 'portfolio') {
      return {
        kind: 'portfolio',
        portfolioId: formData.reportObject || '',
        portfolioName: portfolioName ?? formData.portfolioName ?? '',
        companyIds: companyIds ?? [],
      };
    }

    const companyId = (formData.reportObject || '').replace(/^company:/, '');
    return {
      kind: 'company',
      companyId,
    };
  }
}
